/**
 * Export dropdown, replacing the previous app's two header buttons (CSV, PDF):
 * the full analysis CSV (also the E key), each section as its own CSV, the
 * budget projection when one exists, and the PDF report.
 */
import { DropdownMenu } from 'radix-ui';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { IconButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import {
  exportBudgetCsv,
  exportConflictsCsv,
  exportContactWindowsCsv,
  exportCsvQuick,
  exportMatrixCsv,
  exportSummaryCsv,
  exportUtilisationCsv,
} from '../export/csv';
import { useExportState } from '../export/exportStore';
import { exportPdf } from '../export/pdf';
import { useLoader } from '../store';

const itemClass =
  'flex cursor-pointer select-none items-center gap-2 rounded-control px-2.5 py-1.5 text-xs text-ink outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 data-[highlighted]:bg-overlay';

export function ExportMenu({ className }: { className?: string }) {
  const hasResults = useLoader((s) => s.results !== null);
  const hasProjection = useExportState((s) => s.budgetProjection !== null);

  const items: { key: string; label: string; icon: typeof FileSpreadsheet; run: () => void; enabled: boolean }[] = [
    { key: 'all', label: 'Full analysis CSV (E)', icon: FileSpreadsheet, run: exportCsvQuick, enabled: hasResults },
    { key: 'matrix', label: 'Matrix CSV', icon: FileSpreadsheet, run: exportMatrixCsv, enabled: hasResults },
    { key: 'util', label: 'Utilisation CSV', icon: FileSpreadsheet, run: exportUtilisationCsv, enabled: hasResults },
    { key: 'conflicts', label: 'Conflicts CSV', icon: FileSpreadsheet, run: exportConflictsCsv, enabled: hasResults },
    {
      key: 'windows',
      label: 'Contact windows CSV',
      icon: FileSpreadsheet,
      run: exportContactWindowsCsv,
      enabled: hasResults,
    },
    { key: 'summary', label: 'Summary CSV', icon: FileSpreadsheet, run: exportSummaryCsv, enabled: hasResults },
  ];
  if (hasProjection) {
    items.push({ key: 'budget', label: 'Budget CSV', icon: FileSpreadsheet, run: exportBudgetCsv, enabled: true });
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <IconButton label="Export" className={className}>
          <Download className="h-4 w-4" />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[220px] rounded-panel border border-hairline-strong bg-raised p-1 shadow-elev-2"
        >
          <DropdownMenu.Label className="px-2.5 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-ink-3">
            {hasResults ? 'Export the current analysis' : 'Run an analysis to export'}
          </DropdownMenu.Label>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <DropdownMenu.Item
                key={item.key}
                disabled={!item.enabled}
                onSelect={() => item.run()}
                className={itemClass}
              >
                <Icon className="h-3.5 w-3.5 text-ink-2" />
                {item.label}
              </DropdownMenu.Item>
            );
          })}
          <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
          <DropdownMenu.Item disabled={!hasResults} onSelect={() => exportPdf()} className={cn(itemClass)}>
            <FileText className="h-3.5 w-3.5 text-ink-2" />
            PDF report
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
